import { useTranslation } from "react-i18next";
import { Mail, Phone } from "lucide-react";
import {
  TenantNotAvailableReason,
  TenantNotAvailableInfo,
} from "../../../util/tenantConfig";

// kolula support contact. Hardcoded (not from tenant config) because the
// not-found screen renders precisely when no tenant could be resolved.
const SUPPORT_EMAIL = "support@kolula.com";
const SUPPORT_PHONE_DISPLAY = "+49 211 542435 40";
const SUPPORT_PHONE_TEL = "+4921154243540";

// tel: hrefs must be digits/+ only — strip spaces and formatting.
const telHref = (phone: string): string => `tel:${phone.replace(/[^\d+]/g, "")}`;

interface TenantNotAvailableProps {
  /**
   * Why no tenant resolved:
   *   - "not_found": wrong/unknown link — show kolula support contact.
   *   - "inactive": tenant exists but its booking page isn't live yet —
   *     show the tenant's branding + the tenant's own contact.
   * Defaults to the wrong-link variant.
   */
  reason?: TenantNotAvailableReason;
  /** Tenant identity for the "inactive" variant (name, logo, own contact). */
  info?: TenantNotAvailableInfo | null;
}

/**
 * Shown on the shared deployment when the URL slug resolves to no live tenant.
 * Renders without the tenant theme (and before i18n is initialized for a
 * tenant), so the inline English defaults are the effective copy.
 */
const TenantNotAvailable = ({ reason = "not_found", info }: TenantNotAvailableProps) => {
  const { t } = useTranslation();

  if (reason === "inactive") {
    const organizationName = info?.organizationName?.trim();
    const logoUrl = info?.logoUrl ?? undefined;
    const phone = info?.contactPhone?.trim();
    const email = info?.contactEmail?.trim();

    const phoneLink = phone ? (
      <a className="underline" href={telHref(phone)}>
        {phone}
      </a>
    ) : null;
    const emailLink = email ? (
      <a className="underline" href={`mailto:${email}`}>
        {email}
      </a>
    ) : null;

    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={organizationName ?? ""}
            className="mb-2 max-h-20 w-auto object-contain"
          />
        ) : null}
        {organizationName ? (
          <h1 className="text-xl font-semibold">{organizationName}</h1>
        ) : null}
        <p className="max-w-md text-gray-600">
          {t(
            "tenantNotAvailable.inactive.message",
            "The booking page is momentarily inactive.",
          )}
        </p>
        {(phoneLink || emailLink) && (
          <p className="max-w-md text-gray-600">
            {t(
              "tenantNotAvailable.inactive.contactLead",
              "To make a reservation please contact us",
            )}{" "}
            {phoneLink && <>at {phoneLink}</>}
            {phoneLink && emailLink && <> {t("tenantNotAvailable.inactive.or", "or")} </>}
            {emailLink && <>at {emailLink}</>}
            {"."}
          </p>
        )}
      </div>
    );
  }

  // not_found — wrong/unknown link: point the visitor at kolula support.
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
      <h1 className="text-xl font-semibold">
        {t("tenantNotAvailable.notFound.title", "This booking page doesn't exist")}
      </h1>
      <p className="max-w-md text-gray-600">
        {t(
          "tenantNotAvailable.notFound.description",
          "The link may be incorrect or no longer exists. If you think this is a mistake, please contact our support team:",
        )}
      </p>
      <div className="mt-2 flex flex-col items-center gap-2 text-gray-700">
        <a
          className="inline-flex items-center gap-2 underline"
          href={`mailto:${SUPPORT_EMAIL}`}
        >
          <Mail className="h-4 w-4" aria-hidden="true" />
          {SUPPORT_EMAIL}
        </a>
        <a
          className="inline-flex items-center gap-2 underline"
          href={`tel:${SUPPORT_PHONE_TEL}`}
        >
          <Phone className="h-4 w-4" aria-hidden="true" />
          {SUPPORT_PHONE_DISPLAY}
        </a>
      </div>
    </div>
  );
};

export default TenantNotAvailable;
